import flatstore from "flatstore";
import { isNil } from "lodash";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SubscriptionCustomizeLoader from "../../../assets/contentLoader/SubscriptionCustomizeLoader";
import farmersImgchicken from "../../../assets/img/subscription/farmer_chicken.jpg";
import farmersImgcow from "../../../assets/img/subscription/farmer_cow.jpg";
import farmersImgfish from "../../../assets/img/subscription/farmer_fish.jpg";
import farmersImgpig from "../../../assets/img/subscription/farmer_pig.jpg";
import Image from "../../../components/common/Image";
import W from "../../../components/common/Word";
import {
  getSubscriptionPaths,
  localStorageUtil,
} from "../../../utils/functions";
import { getSubscriptionProductList } from "../../../utils/services";

const calculateTotalItems = (items) =>
  Object.values(items).reduce((total, item) => total + item.quantity, 0);

const itemSelectionReducer = (state, action) => {
  let newState = { ...state };
  switch (action.type) {
    case "add":
      // Ensure not to exceed maxItems
      if (newState.totalItems >= newState.maxItems) return newState;
      const quantityAdd = newState.items[action.itemId]
        ? newState.items[action.itemId].quantity + 1
        : 1;
      newState.items[action.itemId] = {
        product_id: action.productId,
        quantity: quantityAdd,
        title: action.title,
        weight: action.weight,
        unit_type: action.unit_type,
        pieces_per_vac: action.pieces_per_vac,
        subscription_amount: action.subscription_amount,
      };
      break;
    case "remove":
      if (
        newState.items[action.itemId] &&
        newState.items[action.itemId].quantity > 1
      ) {
        newState.items[action.itemId].quantity -= 1;
      } else if (newState.items[action.itemId]) {
        delete newState.items[action.itemId];
      }
      break;
    case "reset":
      newState.items = {};
      break;
    case "updateMaxItems":
      newState.maxItems = action.maxItems;
      break;
    default:
      return newState;
  }
  newState.totalItems = calculateTotalItems(newState.items);
  return newState;
};

function CustomizeSubscription({ subscription }) {
  const [data, setData] = useState([]);
  const [submissionNotice, setSubmissionNotice] = useState({
    message: "",
    className: "",
  });
  const [maxQtyMsgVisible, setMaxQtyMsgVisible] = useState({});
  const [isCountBoxOpen, setIsCountBoxOpen] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const paths = getSubscriptionPaths();

  const size = localStorageUtil.getItem("subscription")?.size || "";
  const type = "customize";

  const [maxItemsReached, setMaxItemsReached] = useState(false);
  const [maxItemsLimit, setMaxItemsLimit] = useState(
    size === "small" ? 11 : 20
  );

  const isResetting =
    sessionStorage.getItem("resetting_subscription") === "true";

  const [selectedItems, dispatch] = useReducer(
    itemSelectionReducer,
    {
      // items: localStorageUtil.getItem("subscription")?.items || {},
      items: {},
      totalItems: 0,
      maxItems: maxItemsLimit,
    },
    (initState) => ({
      ...initState,
      totalItems: calculateTotalItems(initState.items),
    })
  );

  const animal_types = {
    bioCow: 1,
    cow: 2,
    chicken: 3,
    pig: 4,
    sheep: 5,
    duck: 6,
    fish: 7,
    deer: 8,
  };

  useEffect(() => {
    if (!size) {
      navigate(paths.base || "/");
    }

    localStorageUtil.updateObjectItem("subscription", {
      items: selectedItems.items,
    });
    // Update maxItemsReached based on totalItems and maxItems

    if (selectedItems.totalItems >= selectedItems.maxItems) {
      setMaxItemsReached(true);
    } else {
      setMaxItemsReached(false);
    }
  }, [selectedItems]);

  useEffect(() => {
    if (isNil(subscription)) {
      getSubscriptionProductList();
    }
    if (subscription && !isNil(subscription)) {
      // Filter data based on subscription_type and total_stock
      let filteredData = subscription.filter((item) => {
        return (
          item.subscription_type === "grutto_subscription_custom_" + size &&
          item.total_stock !== 0
        );
      });

      // Map animal types according to mapping
      const animalTypeMap = {
        boar: "game",
        roe: "game",
        deer: "game",
        salmon: "fish",
        codfish: "fish",
      };

      filteredData = filteredData.map((item) => ({
        ...item,
        animal_type: animalTypeMap[item.animal_type] || item.animal_type,
      }));

      // console.log(filteredData);

      // Sort filteredData based on animal_type
      filteredData.sort((a, b) =>
        a.animal_type === b.animal_type
          ? 0
          : animal_types[a.animal_type] > animal_types[b.animal_type]
          ? 1
          : -1
      );

      setData(filteredData);

      if (typeof filteredData[0]?.bundle_size !== "undefined") {
        let maxItemsNum = Number(filteredData[0].bundle_size);
        setMaxItemsLimit(maxItemsNum);
        dispatch({ type: "updateMaxItems", maxItems: maxItemsNum });
      }

      const prefilledItems =
        localStorageUtil.getItem("subscription")?.prefilledItems || null;
      if (prefilledItems) {
        Object.entries(prefilledItems).forEach(([itemSlug, addedQuantity]) => {
          const dataItem = filteredData.find(
            (dataItem) => dataItem.slug === itemSlug
          );
          if (dataItem) {
            // Dispatch 'add' action addedQuantity times
            for (let i = 0; i < +addedQuantity; i++) {
              dispatch({
                type: "add",
                itemId: dataItem.bundled_item_id,
                title: dataItem.title,
                weight: dataItem.weight,
                unit_type: dataItem.unit_type,
                pieces_per_vac: dataItem.pieces_per_vac,
                productId: dataItem.meatcut_product_id,
                subscription_amount: dataItem.subscription_amount,
              });
            }
          }
        });
        localStorageUtil.updateObjectItem("subscription", {
          prefilledItems: null,
        });
      }
    }
  }, [size, subscription]);

  // hide submissionNotice after certain time
  useEffect(() => {
    let timeout;
    if (submissionNotice) {
      timeout = setTimeout(() => {
        setSubmissionNotice({ message: "", className: "" });
      }, 3000);
    }
    return () => clearTimeout(timeout);
  }, [submissionNotice, setSubmissionNotice]);

  useEffect(() => {
    if (data.length > 0) {
      // Data is available, set up the references
      assignRefs();
    }
  }, [data]);

  const animalTypes = [...new Set(data.map((item) => item.animal_type))];
  const animalGroupRefs = useRef({});
  const assignRefs = () => {
    // Assign refs for each animal group element
    animalTypes.forEach((type) => {
      animalGroupRefs.current[type] = React.createRef();
    });
  };

  useEffect(() => {
    // Flag to check if all selected items exist in data
    let allItemsExist = true;

    if (data.length) {
      // Iterate over each selectedItem to check if it exists in data based on bundled_item_id
      Object.keys(selectedItems.items).forEach((itemKey) => {
        const existsInData = data.some(
          (dataItem) => dataItem.bundled_item_id.toString() === itemKey
        );
        if (!existsInData) {
          allItemsExist = false;
        }
      });
    }

    if (
      data.length &&
      (selectedItems.totalItems > maxItemsLimit || !allItemsExist)
    ) {
      dispatch({ type: "reset" });
    }
  }, [selectedItems, data]);

  const updateMaxQtyMsgVisibility = (id, isVisible) => {
    setMaxQtyMsgVisible((prev) => ({
      ...prev,
      [id]: isVisible,
    }));
    if (isVisible) {
      setTimeout(() => {
        setMaxQtyMsgVisible((prev) => ({
          ...prev,
          [id]: false,
        }));
      }, 3000);
    }
  };

  const handleItemSelection = useCallback(
    (
      itemId,
      productId,
      maxQty,
      actionType,
      title,
      weight,
      unit_type,
      pieces_per_vac,
      subscription_amount,
    ) => {
      if (actionType === "add") {
        const currentItemQuantity = selectedItems.items[itemId]?.quantity ?? 0;
        if (currentItemQuantity >= parseInt(maxQty)) {
          updateMaxQtyMsgVisibility(itemId, true); // Show max quantity message
          return;
        }
      }
      // Otherwise, proceed with item selection
      if (
        actionType === "add" &&
        selectedItems.totalItems >= selectedItems.maxItems
      ) {
        setMaxItemsReached(true);
        setSubmissionNotice({
          message: <W>subscription.response.success</W>,
          className: "success",
        });
        return; // Early return to prevent adding more items
      }
      dispatch({
        type: actionType,
        itemId,
        productId,
        title,
        weight,
        unit_type,
        pieces_per_vac,
        subscription_amount,
      });
    },
    [selectedItems, maxQtyMsgVisible]
  ); // Add all dependencies here

  /**
   * Handle submitted items
   */
  const handleSubmit = () => {
    if (maxItemsReached) {
      navigate(paths.steps["period"] || "/"); // Fallback to root if path is undefined
    } else {
      setSubmissionNotice({
        message: `Please add ${
          selectedItems.maxItems - selectedItems.totalItems
        } more item(s) to proceed.`,
        className: "error",
      });
    }
  };

  const handleCountBoxClick = () => {
    setIsCountBoxOpen(!isCountBoxOpen);
  };

  // Box Precentage
  const fillPercentage =
    (selectedItems.totalItems / selectedItems.maxItems) * 100;
  const boxFill = {
    height: `${Math.min(fillPercentage, 100)}%`, // Ensure it doesn't exceed 100%
  };

  // Progress bar calculation based on item selection
  const progressBarPercentage =
    25 + (selectedItems.totalItems / selectedItems.maxItems) * 50; // Increase from 50% to 75%
  const progressBarWidth = {
    width: `${progressBarPercentage}%`,
  };

  if (!data.length) {
    return (
      <div className="subscription-box">
        <div className="container pt-5 pb-5">
          <div className="row">
            <div className="col-12">
              <SubscriptionCustomizeLoader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="subscription-box">
        <div className="progress-bar">
          <div className="fill" style={progressBarWidth}></div>
        </div>

        {submissionNotice.message && (
          <div className={`response ${submissionNotice.className}`}>
            {submissionNotice.message}
          </div>
        )}

        <div className="container">
          <div className="mt-4 text-center">
            <h2 className="heading">
              {" "}
              {isResetting
                ? t("subscription.customize.page.title-update", {})
                : t("subscription.customize.page.title", {
                    num: selectedItems.maxItems,
                  })}
            </h2>
          </div>
        </div>

        <div className="container farmshop-items">
          <div className="row">
            <div
              className={`pack-overview ${
                Object.values(selectedItems.items).length === 0
                  ? "empty"
                  : "has-items"
              } ${isCountBoxOpen ? "open" : ""}`}
            >
              <div className="cta">
                <div className="count" onClick={handleCountBoxClick}>
                  <span className="ico">
                    <span className="ico-fill" style={boxFill}></span>
                  </span>
                  <span className="numbers">
                    {`${selectedItems.totalItems}` +
                      " " +
                      t("subscription.of") +
                      " " +
                      `${selectedItems.maxItems}`}
                  </span>
                  <span className="ml-1 d-none d-md-block">
                    <W>subscription.products_chosen</W>
                  </span>
                </div>

                {maxItemsReached ? (
                  <button className="btn btn--red" onClick={handleSubmit}>
                    {t("subscription." + type + ".cta")}
                  </button>
                ) : (
                  <button className="btn btn--disable">
                    {" "}
                    {t("subscription." + type + ".cta")}{" "}
                  </button>
                )}
              </div>
              <div className="overview-items">
                <div className="overview-items-holder">
                  <div
                    className={`subscription__cta2 customizeCta loading ${
                      Object.values(selectedItems.items).length === 0
                        ? "empty"
                        : "has-items"
                    }`}
                  >
                    <div className="title m-0">
                      <W>subscription.overview.title</W>
                    </div>
                    {
                    /* {Object.values(selectedItems.items).length == 0 && (
                      <div className="empty-cart">
                        <W>subscription.overview.empty_cart</W>
                      </div>
                    )} */}
                    {Object.values(selectedItems.items).map((item) => (
                      <div className="itm mb-2" key={item.bundled_item_id}>
                        <div className="h-title">
                          <span className="name">{item.title}</span>
                          <span className="info">
                            {`~${item.weight}${item.unit_type || "g"} x ${item.subscription_amount * item.pieces_per_vac} `}{" "}
                            <W>farmshop.overview.pcs</W>
                          </span>
                        </div>
                        <div className={`quantity  enabled`}>
                          <a
                            className={`plus-btn `}
                            role="button"
                            onClick={() => {
                              const dataItem = data.find(
                                (dataItem) =>
                                  dataItem.meatcut_product_id ===
                                  item.product_id
                              );

                              if (dataItem) {
                                handleItemSelection(
                                  dataItem.bundled_item_id,
                                  dataItem.meatcut_product_id,
                                  dataItem.max_quantity,
                                  "add",
                                  dataItem.title,
                                  dataItem.weight,
                                  dataItem.unit_type,
                                  dataItem.pieces_per_vac,
                                  dataItem.subscription_amount
                                );
                              }
                            }}
                          >
                            <i className="icon-plus"></i>
                          </a>
                          <span className="number">
                            <input
                              type="text"
                              value={item.quantity.toString() || ""}
                              disabled
                            />
                          </span>
                          <a
                            className="minus-btn"
                            role="button"
                            onClick={() => {
                              const dataItem = data.find(
                                (dataItem) =>
                                  dataItem.meatcut_product_id ===
                                  item.product_id
                              );

                              if (dataItem) {
                                handleItemSelection(
                                  dataItem.bundled_item_id,
                                  dataItem.meatcut_product_id,
                                  dataItem.max_quantity,
                                  "remove",
                                  dataItem.title,
                                  dataItem.weight,
                                  dataItem.unit_type,
                                  dataItem.pieces_per_vac,
                                  dataItem.subscription_amount
                                );
                              }
                            }}
                          >
                            <i className="icon-minus"></i>
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="col">
              <div className="bundle__items">
                {/* Render animal groups and their items */}
                {animalTypes.map((animalType) => (
                  <Fragment key={animalType}>
                    <div className={`animal_group animal_group_${animalType}`}>
                      <a name={`products_${animalType}`}></a>
                      <h2 className="title m-0">
                        <span className="name">
                          {t(animalType).charAt(0).toUpperCase() +
                            t(animalType).slice(1)}
                        </span>
                        <span className="sep">-</span>
                        <span className="desc"> {t(animalType + "_desc")}</span>
                      </h2>
                      <hr
                        ref={animalGroupRefs.current[animalType]}
                        id={`animal_type_${animalType}`}
                      />
                    </div>

                    {/* Render items for the current animal type */}
                    {data
                      .filter((item) => item.animal_type === animalType)
                      .map((item) => (
                        <div
                          className={
                            "farmshop__item " +
                            (item.is_organic == 1 ? "organic" : "")
                          }
                          key={item.bundled_item_id}
                        >
                          <div className="farmshop__title">
                            <span className="title">{item.title}</span>
                          </div>
                          <figure className="img m-0">
                            {maxQtyMsgVisible[item.bundled_item_id] ? (
                              <>
                                <div className="msg">
                                  {t("subscription.response.maximum_quantity")}
                                </div>
                              </>
                            ) : (
                              ""
                            )}
                            <Image
                              src={item.image_url}
                              alt={item.title}
                              title={item.title}
                              className="img-fluid"
                            />
                          </figure>
                          <div className="farmshop__item__farmer px-2">
                            <div className="farmshop_farmer_image">
                              <img
                                src={
                                  item.animal_type === "cow" ||
                                  item.animal_type === "bioCow"
                                    ? farmersImgcow
                                    : item.animal_type === "pig"
                                    ? farmersImgpig
                                    : item.animal_type === "chicken"
                                    ? farmersImgchicken
                                    : item.animal_type === "fish"
                                    ? farmersImgfish
                                    : farmersImgcow // default fallback
                                }
                                width="100"
                                height="100"
                                alt={t("subscription.farmer_name")}
                                title={t("subscription.farmer_name")}
                              />
                            </div>
                            <div className="farmshop_farmer_name">
                              <W>farmers.{item.animal_type}</W>:{" "}
                              <W>subscription.farmer_name.{item.animal_type}</W>
                            </div>
                          </div>
                          <div className="body p-2">
                            <div className="subscription__item__info">
                              <span className="weight">
                                <span className="number">
                                  {item.subscription_amount ? (
                                    <>
                                      {item.subscription_amount}
                                      <span className="fw-normal">x</span>
                                      {item.pieces_per_vac}
                                    </>
                                  ) : (
                                    item.pieces_per_vac
                                  )}
                                </span>{" "}
                                <W>subscription.pieces</W>{" "}
                                <span>
                                  {item.weight}{" "}
                                  <W>
                                    farmshop.unit_txt.
                                    {item.unit_type ? item.unit_type : "g"}
                                  </W>
                                </span>
                              </span>

                              <span className="total">
                                {(item.subscription_amount
                                  ? item.subscription_amount *
                                    item.pieces_per_vac
                                  : item.pieces_per_vac) * item.weight}
                                <W>
                                  farmshop.unit_txt.
                                  {item.unit_type ? item.unit_type : "g"}
                                </W>
                              </span>
                            </div>
                            <div
                              className={`quantity product-${
                                item.bundled_item_id
                              } ${
                                selectedItems.items[item.bundled_item_id]
                                  ?.quantity > 0
                                  ? "enabled"
                                  : ""
                              }`}
                            >
                              <a
                                className={`plus-btn ${
                                  selectedItems.items[item.bundled_item_id]
                                    ?.quantity >= item.max_quantity &&
                                  item.max_quantity !== ""
                                    ? "btn--disable"
                                    : ""
                                }`}
                                role="button"
                                onClick={() =>
                                  handleItemSelection(
                                    item.bundled_item_id,
                                    item.meatcut_product_id,
                                    item.max_quantity,
                                    "add",
                                    item.title,
                                    item.weight,
                                    item.unit_type,
                                    item.pieces_per_vac,
                                    item.subscription_amount
                                  )
                                }
                              >
                                <i className="icon-plus"></i>
                              </a>
                              <span className="number">
                                <input
                                  type="text"
                                  value={
                                    selectedItems.items[
                                      item.bundled_item_id
                                    ]?.quantity.toString() || ""
                                  }
                                  disabled
                                />
                              </span>
                              <a
                                className="minus-btn"
                                role="button"
                                onClick={() =>
                                  handleItemSelection(
                                    item.bundled_item_id,
                                    item.meatcut_product_id,
                                    item.max_quantity,
                                    "remove",
                                    item.title,
                                    item.weight,
                                    item.unit_type,
                                    item.pieces_per_vac,
                                    item.subscription_amount
                                  )
                                }
                              >
                                <i className="icon-minus"></i>
                              </a>
                            </div>
                          </div>
                        </div>
                      ))}
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default flatstore.connect(["subscription"])(CustomizeSubscription);
